import { logWarning } from '@databases/utils/utils';
import React, { useState, useEffect } from 'react';
import { IntlProvider } from 'react-intl-next';

import { getExternalDatabasesMessages } from '@canvas/external-messages';
import {
  fetchLocaleMessages,
  getExternalMessagesPromiseFromLoaders,
  Product,
} from '@canvas/intl';

import { getBrowserDefaultLocale } from '../constants';

export interface IntlProviderWithMessagesProps {
  children: React.ReactNode;
  locale: string;
}

export const IntlProviderWithMessages: React.FunctionComponent<
  IntlProviderWithMessagesProps
> = (props) => {
  const { children, locale } = props;
  const [localeMessages, setLocaleMessages] = useState({});
  const [loadedMessages, setLoadedMessages] = useState(false);

  useEffect(() => {
    (async () => {
      const productMessagesPromise = fetchLocaleMessages(Product.DATABASES, locale).catch(
        (error: unknown) => {
          if (error instanceof Error) {
            logWarning('Error loading product messages', error);
          }
          return {};
        }
      );
      const externalMessagePromises = getExternalMessagesPromiseFromLoaders(
        getExternalDatabasesMessages(locale)
      );

      try {
        const [messages, ...external] = await Promise.all([
          productMessagesPromise,
          ...externalMessagePromises,
        ]);

        let externalMessages = {};
        for (const { messages } of external) {
          externalMessages = { ...messages, ...externalMessages };
        }

        setLocaleMessages({
          ...externalMessages,
          ...messages,
        });
        setLoadedMessages(true);
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (error) {
        if (error instanceof Error) {
          logWarning('Error loading messages', error);
        }
      }
    })();
  }, [locale]);

  if (!loadedMessages) {
    return null;
  }

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={getBrowserDefaultLocale()}
      messages={localeMessages}
      defaultRichTextElements={{
        code: (chunks) => <code>{chunks}</code>,
        br: () => <br />,
        strong: (chunks) => <strong>{chunks}</strong>,
      }}
      wrapRichTextChunksInFragment
    >
      {children}
    </IntlProvider>
  );
};
